import { useCallback, useMemo } from 'react';

import { storeKeys } from '@givebacks/api/store';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, ModalStackReturnType } from 'ui';

import { NewOrderNoteModal } from '../NewOrderNoteModal/NewOrderNoteModal';
import { RefundOrderModal } from '../RefundOrderModal';
import { ResendReceiptModal } from '../ResendReceiptModal';
import { FullOrderModal } from './FullOrderModal';
import { FullOrderModalProps } from './FullOrderModal.types';
import { FullOrderModalStackModals } from './useFullOrderModalStack';

interface FullOrderModalStackProps extends Omit<FullOrderModalProps, 'onClose' | 'opened'> {
  stack: ModalStackReturnType<FullOrderModalStackModals>;
}

const FullOrderModalStack = ({ stack, order, posOrderActions }: FullOrderModalStackProps) => {
  const queryClient = useQueryClient();

  const isAllItemsRefunded = useMemo(() => {
    return order?.cart_items?.every((item) => item.reversed_at);
  }, [order?.cart_items]);

  const isRefundable = useMemo(() => {
    return (
      (order?.status == 'purchased' || order?.status === 'partially_refunded') &&
      !isAllItemsRefunded
    );
  }, [order?.status, isAllItemsRefunded]);

  const handleSuccess = useCallback(() => {
    if (!order?.cause_id) return null;
    void queryClient.invalidateQueries({ queryKey: storeKeys.id(order?.cause_id).carts.items.all });
  }, [order?.cause_id, queryClient]);

  const handleOpenNewOrderSuccesses = useCallback(() => {
    if (!order?.cause_id) return null;
    void queryClient.invalidateQueries({
      queryKey: storeKeys.id(order?.cause_id).carts.id(order.uuid).detail,
    });
  }, [order?.cause_id, order.uuid, queryClient]);

  return (
    <Modal.Stack>
      <FullOrderModal
        order={order}
        onResendReceipt={
          order.status === 'purchased'
            ? () => stack.open(FullOrderModalStackModals.ResendReceipt)
            : undefined
        }
        onCancelPledge={
          order.status === 'pledged'
            ? () => stack.open(FullOrderModalStackModals.RefundOrder)
            : undefined
        }
        onRefund={
          isRefundable ? () => stack.open(FullOrderModalStackModals.RefundOrder) : undefined
        }
        onOpenNewOrderNoteModal={() => stack.open(FullOrderModalStackModals.NewOrderNote)}
        posOrderActions={posOrderActions}
        {...stack.register(FullOrderModalStackModals.FullOrder)}
      />

      <ResendReceiptModal
        cart={order}
        {...stack.register(FullOrderModalStackModals.ResendReceipt)}
      />

      <RefundOrderModal
        cartId={order.uuid}
        causeId={order.cause_id}
        onSuccess={handleSuccess}
        {...stack.register(FullOrderModalStackModals.RefundOrder)}
      />

      <NewOrderNoteModal
        causeId={order.cause_id}
        cartId={order.uuid}
        onSave={() => {
          handleOpenNewOrderSuccesses();
          stack.close(FullOrderModalStackModals.NewOrderNote);
        }}
        {...stack.register(FullOrderModalStackModals.NewOrderNote)}
      />
    </Modal.Stack>
  );
};

export { FullOrderModalStack };
